import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Loader2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatTime12(t) {
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Shared dialog to edit patient name/phone and reschedule (date/time).
// `onSave(name, phone, date, time)` must perform the API call and throw on failure.
export const EditContactDialog = ({ appt, onSave, testIdPrefix = "edit" }) => {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState(appt.patient_name || "");
    const [phone, setPhone] = useState(appt.phone || "");
    const [date, setDate] = useState(appt.date);
    const [time, setTime] = useState(appt.time);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [saving, setSaving] = useState(false);

    const openDialog = () => {
        setName(appt.patient_name || "");
        setPhone(appt.phone || "");
        setDate(appt.date);
        setTime(appt.time);
        setOpen(true);
    };

    // Free slots for the chosen date (keeps the appt's own slot selectable on its original date)
    useEffect(() => {
        if (!open || !date) return;
        let cancelled = false;
        setLoadingSlots(true);
        axios
            .get(`${API}/appointments/availability`, { params: { date } })
            .then((res) => {
                if (cancelled) return;
                const free = res.data.is_open ? res.data.available_slots : [];
                const options = date === appt.date && !free.includes(appt.time)
                    ? [appt.time, ...free].sort()
                    : free;
                setSlots(options);
                setTime((t) => (options.includes(t) ? t : options[0] || ""));
            })
            .catch(() => {
                if (!cancelled) setSlots([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingSlots(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, date]);

    const save = async () => {
        const digits = (phone || "").replace(/\D/g, "");
        if (!name.trim() || name.trim().length < 2) {
            toast.error("Escribe el nombre del paciente");
            return;
        }
        if (digits.length < 10) {
            toast.error("El teléfono debe tener al menos 10 dígitos");
            return;
        }
        if (!date || !time) {
            toast.error("Selecciona fecha y horario");
            return;
        }
        setSaving(true);
        try {
            await onSave(name.trim(), phone.trim(), date, time);
            toast.success("Cita actualizada ✔");
            setOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo actualizar la cita");
        } finally {
            setSaving(false);
        }
    };

    const moved = date !== appt.date || time !== appt.time;

    return (
        <>
            <button
                onClick={openDialog}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                title="Editar cita (datos / reagendar)"
                data-testid={`${testIdPrefix}-contact-btn-${appt.id}`}
            >
                <Pencil className="h-4 w-4" />
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-2xl sm:max-w-md" data-testid={`${testIdPrefix}-contact-dialog`}>
                    <DialogHeader>
                        <DialogTitle>Editar cita</DialogTitle>
                        <DialogDescription>
                            {appt.procedure_name} · originalmente {appt.date} a las {formatTime12(appt.time)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                                Nombre del paciente
                            </label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="h-11 rounded-xl"
                                data-testid={`${testIdPrefix}-contact-name`}
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                                Teléfono (10 dígitos)
                            </label>
                            <Input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="444 123 4567"
                                className="h-11 rounded-xl"
                                data-testid={`${testIdPrefix}-contact-phone`}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                                    Fecha
                                </label>
                                <Input
                                    type="date"
                                    value={date}
                                    min={todayISO()}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="h-11 rounded-xl"
                                    data-testid={`${testIdPrefix}-contact-date`}
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                                    Horario
                                </label>
                                <select
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    disabled={loadingSlots || slots.length === 0}
                                    className="h-11 w-full rounded-xl border border-input bg-background px-2 text-sm"
                                    data-testid={`${testIdPrefix}-contact-time`}
                                >
                                    {slots.map((s) => (
                                        <option key={s} value={s}>
                                            {formatTime12(s)}
                                            {s === appt.time && date === appt.date ? " (actual)" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {loadingSlots ? (
                            <p className="text-xs text-muted-foreground">Consultando horarios libres...</p>
                        ) : slots.length === 0 ? (
                            <p className="text-xs text-destructive" data-testid={`${testIdPrefix}-no-slots`}>
                                No hay horarios disponibles ese día. Elige otra fecha.
                            </p>
                        ) : moved ? (
                            <p className="text-xs font-medium text-primary" data-testid={`${testIdPrefix}-moved-hint`}>
                                📅 La cita se moverá a {date} a las {formatTime12(time)}
                            </p>
                        ) : null}
                        <div className="flex gap-2 justify-end pt-1">
                            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
                                Cancelar
                            </Button>
                            <Button
                                onClick={save}
                                disabled={saving || slots.length === 0}
                                className="rounded-xl"
                                data-testid={`${testIdPrefix}-contact-save`}
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4 mr-1.5" />
                                )}
                                Guardar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
